import { t } from "i18next";
import { useCallback, useState } from "react";

import Button from "~/components/buttons/Button";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import { deleteDraftNonProjectInvoice } from "~/modules/billing/api/draftInvoice/draftInvoiceApiDispatchers.ts";
import InvoiceDetailsElement from "~/modules/billing/components/InvoiceDetailsElement";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	invoiceData: UnprocessedInvoicesIndexTableData;
};

const DeleteNonProjectDraftInvoiceForm = ({
	onCancel,
	onSuccess,
	invoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const onSubmit = useCallback(async () => {

		try {
			setIsBusy(true);
			await deleteDraftNonProjectInvoice(invoiceData.id);
			onSuccess();
		} catch (error: unknown) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceData.id, onSuccess]);

	return (<>
		<SidebarHeader>
			<SidebarHeaderHeadline>Nicht projektbezogene Rechnung löschen</SidebarHeaderHeadline>
		</SidebarHeader>
		<SidebarContent>
			{isBusy && <SidebarBusyOverlay />}
			{!!serverErrorMessage &&
				<SidebarErrorOverlay title="Löschen fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
			<dl className="divide-y divide-gray-100">
				<InvoiceDetailsElement label="Beschreibung">
					{invoiceData.projectTitle}
				</InvoiceDetailsElement>
				<InvoiceDetailsElement label="Empfänger">
					{invoiceData.recipientInformation || EMPTY_VALUE_PLACEHOLDER}
				</InvoiceDetailsElement>
				<InvoiceDetailsElement label="Leistungszeitraum">
					{formatSmartDateRange(invoiceData.deliveryStartDate, invoiceData.deliveryEndDate, {
						month: "MMMM",
						year: " yyyy",
					})}
				</InvoiceDetailsElement>
				<InvoiceDetailsElement label="Rechnungssumme (netto)">
					{formatCentsToCurrency(invoiceData.invoiceTotalCents)}
				</InvoiceDetailsElement>
				<InvoiceDetailsElement label="Kommentar">
					{invoiceData.comment || EMPTY_VALUE_PLACEHOLDER}
				</InvoiceDetailsElement>
			</dl>
		</SidebarContent>
		<SidebarFooter>
			<Button theme="danger" onClick={onSubmit}>
				Rechnung löschen
			</Button>
			<Button theme="white"
					onClick={onCancel}>
				Abbrechen
			</Button>
		</SidebarFooter></>);
};

export default DeleteNonProjectDraftInvoiceForm;
